import { useCallback, useMemo } from "react";
import useSWR from "swr";
import { useSelector } from "react-redux";
import {
  fetchOnboardingStatus,
  setOnboardingHidden,
  setOnboardingStepDismissed,
} from "../api/onboarding";
import {
  ONBOARDING_STEPS,
  ONBOARDING_TOTAL_STEPS,
} from "../../components/onboarding/onboardingSteps";

/**
 * HO-HU-36: estado de la guia de primeros pasos, combinando los textos y
 * destinos del frontend (onboardingSteps.js) con lo que el backend detecto en
 * los datos del condominio.
 *
 * Se usa `useSWR` directo y no el helper `useSWRFetch` porque ese deriva
 * `isLoading` como `!error && !data`, que con la clave en null (rol que no es
 * manager, o sin condominio seleccionado: casos donde no hay que pedir nada)
 * se queda en "cargando" para siempre.
 */
const useOnboardingProgress = () => {
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { role } = useSelector((state) => state.auth.user);

  const enabled = role === "manager" && !!selectedCondominium;

  const { data, error, mutate } = useSWR(
    enabled ? ["onboarding", selectedCondominium] : null,
    () => fetchOnboardingStatus(selectedCondominium)
  );

  const steps = useMemo(() => {
    return ONBOARDING_STEPS.map((step, index) => {
      const status = data?.steps?.[step.key];
      return {
        ...step,
        position: index + 1,
        detected: !!status?.detected,
        dismissed: !!status?.dismissed,
        done: !!status?.done,
        // Un paso solo se puede marcar a mano si no se pudo deducir de los
        // datos: si ya esta detectado, el boton no aporta nada.
        canDismiss: !!step.dismissLabel && !status?.detected,
      };
    });
  }, [data]);

  // El POST responde con el estado completo ya recalculado, asi que se usa
  // como nuevo valor de la cache sin revalidar aparte. El segundo argumento de
  // `mutate` es un booleano (swr 0.5, no el objeto de opciones de swr 2).
  const markDone = useCallback(
    async (key) => {
      if (!enabled) return;
      const updated = await setOnboardingStepDismissed(selectedCondominium, key);
      await mutate(updated, false);
    },
    [enabled, selectedCondominium, mutate]
  );

  const undoStep = useCallback(
    async (key) => {
      if (!enabled) return;
      const updated = await setOnboardingStepDismissed(
        selectedCondominium,
        key,
        false
      );
      await mutate(updated, false);
    },
    [enabled, selectedCondominium, mutate]
  );

  const setHidden = useCallback(
    async (hidden) => {
      if (!enabled) return;
      const updated = await setOnboardingHidden(selectedCondominium, hidden);
      await mutate(updated, false);
    },
    [enabled, selectedCondominium, mutate]
  );

  const completed = data?.completed ?? 0;
  const currentStep = steps.find((step) => !step.done) || null;

  return {
    enabled,
    // Sin condominio seleccionado no hay nada que cargar ni que esperar.
    isLoading: enabled && !data && !error,
    error,
    steps,
    currentStep,
    completed,
    total: data?.total ?? ONBOARDING_TOTAL_STEPS,
    isComplete: !!data && completed >= (data.total ?? ONBOARDING_TOTAL_STEPS),
    // El manager oculto la guia para este condominio. Es aparte de
    // `isComplete`: se puede ocultar con pasos pendientes.
    isHidden: !!data?.hidden,
    aliquot: data?.aliquot ?? null,
    markDone,
    undoStep,
    setHidden,
    refresh: mutate,
  };
};

export default useOnboardingProgress;
